import { Grid, Button, MenuItem, FormControl, DialogTitle, DialogContent, DialogActions, InputLabel, Select, Divider, Typography, ListSubheader, CircularProgress, Skeleton } from "@mui/material";
import { useTranslation } from "react-i18next";
import { StatusCode, useEntities, } from "@emberly/zenith-client";
import { useEffect, useMemo, useState } from "react";
import { ExportEnums, ExportEnumsLists, } from "../../../../common/constants";
import { IntegrationLibrary } from "../../../pages/integrations/common/library";
import ResponsiveDialog from "../../core/ResponsiveDialog";

const FILE_FORMATS_LEN = ExportEnumsLists.InvoiceExportFormats.length;

export default function InvoiceExportDialog(props) {
  const { exportAction, working, dialogAction, exportResult } = props;
  const onClose = () => !!exportResult ? dialogAction.complete(false) : exportAction.throw("cancelled");
  const [name, setName] = useState(null);

  return (
    <ResponsiveDialog
      open
      onClose={!working ? onClose : undefined}
      fullWidth
      maxWidth="xs"
    >

      {
        working ? (
          <ExportWorking />
        ) : !exportResult ? (
          <ExportSetup exportAction={exportAction} setName={setName} />
        ) : (
          <ExportResult dialogAction={dialogAction} exportResult={exportResult} name={name} />
        )
      }

    </ResponsiveDialog>
  );
}



function ExportSetup(props) {
  const { exportAction, setName } = props;

  const { t } = useTranslation();
  const [format, setFormat] = useState(ExportEnums.InvoiceExportFormats.SO01);
  const { entities: integrationEntities, loading: integrationsLoading } = useEntities("Integration");
  const [ loading, setLoading ] = useState(true);


  const integrations = useMemo(
    () => integrationEntities.filter(t => !!IntegrationLibrary.get(t.serviceType)?.capabilities?.ImportInvoiceOrders),
    [integrationEntities]
  );

  useEffect(() => {
    if (!integrationsLoading) {

      if (!!integrations?.length) {
        setFormat(FILE_FORMATS_LEN);
      } 

      setLoading(false);
    }
  }, [integrations?.length !== 0, integrationsLoading]);

  
  const onStartExport = async () => {
    if (format < FILE_FORMATS_LEN) {
      exportAction.complete({ exportType: "file", format, friendlyName: ExportEnumsLists.InvoiceExportFormats[format] });
    } else {
      const integrationIndex = format - FILE_FORMATS_LEN;
      const integration = integrations[integrationIndex];
      setName(integration.serviceName);
      exportAction.complete({ exportType: "integration", integration, friendlyName: integration.friendlyName });
    }
  };

  const onClose = () => exportAction.throw("cancelled");

  return (
    <>
      <DialogTitle>
        {t("order:invoiceFlow:exportDialog:title")}
      </DialogTitle>

      <DialogContent>
        <Grid container spacing={2}>


          <Grid item xs={12}>
            <Typography variant="body2">
              {t("order:invoiceFlow:exportDialog:description")}
            </Typography>
          </Grid>

          <Grid item xs={12}>
            {
              loading ? (
                <Skeleton height="48px" variant="rounded" sx={{ borderRadius: 2 }} />
              ) : (
                <FormControl fullWidth size="small" variant="filled">

                  <InputLabel>{t("invoiceExport:exportSelect")}</InputLabel>
                  <Select onChange={(ev) => setFormat(ev.target.value)} value={format}>

                    <ListSubheader>{t("invoiceExport:filesSubHeader")}</ListSubheader>
                    {ExportEnumsLists.InvoiceExportFormats.map((type, value) => <MenuItem value={value} key={value}>{type}</MenuItem>)}

                    {!!integrations.length ? <Divider /> : null}

                    {!!integrations.length ? <ListSubheader>{t("invoiceExport:integrationsSubHeader")}</ListSubheader> : null}
                    {integrations.map((ig, value) => <MenuItem value={value + FILE_FORMATS_LEN} key={value}>{ig.friendlyName}</MenuItem>)}

                  </Select>
                </FormControl>
              )
            }
          </Grid>


        </Grid>
      </DialogContent>


      <DialogActions>
        <Button onClick={onClose}>
          {t("common:cancel")}
        </Button>
        <Button variant="contained" color="primary" onClick={onStartExport} disabled={false}>
          {t("order:invoiceFlow:exportDialog:confirmText")}
        </Button>
      </DialogActions>
    </>
  );
}

function ExportResult(props) {
  const { dialogAction, exportResult } = props;
  const { t } = useTranslation();

  const success = exportResult.statusCode === StatusCode.Success

  const onClose = () => dialogAction.complete(false);
  const onOpenResultPage = () => dialogAction.complete(true);

  return (<>
    <DialogTitle>
      {success ? t("order:invoiceFlow:exportDialog:successTitle") : t("order:invoiceFlow:exportDialog:errorTitle")}
    </DialogTitle>
    <DialogContent>
      <Grid container spacing={2}>

        <Grid item xs={12}>
          {success ? (
            <>
              <Typography variant="body1">
                {t("order:invoiceFlow:exportDialog:successDescription")}
              </Typography>
              {!!exportResult?.value?.[0]?.actions?.length > 0 ? (
                <ul style={{ margin: 0, paddingLeft: "24px", marginTop: "8px" }}>
                  {exportResult.value[0].actions.map((action, key) => <li key={key}><Typography variant="body1">{action}</Typography></li>)}
                </ul>
              ) : null}
            </>
          ) : (
            <Typography variant="body1">
              {t("order:invoiceFlow:exportDialog:errorDescription")}
              <br />
              "{exportResult?.value ?
                ((exportResult.value.detail || exportResult.value.title || "").replace(/[^A-Z\d\s.:æøåÆØÅ]/gi, '')) :
                !!exportResult?.message ? t(`integrations:errors:${exportResult.message}:title`) : t("order:invoiceFlow:exportDialog:unknownError")
              }"
              <br />
              <br />
              {t("order:invoiceFlow:exportDialog:pleaseFix")}
            </Typography>
          )}

        </Grid>

      </Grid>
    </DialogContent>
    <DialogActions>
      {
        success ? (
          <Button onClick={onOpenResultPage}>
            {t("order:invoiceFlow:exportDialog:openJournal")}
          </Button>
        ) : null
      }
      <Button variant="contained" color={success ? "success" : "error"} onClick={onClose}>
        {success ? t("common:close") : t("common:ok")}
      </Button>
    </DialogActions>
  </>);
}






function ExportWorking() {
  const { t } = useTranslation();

  return (
    <>
      <DialogTitle>
        {t("order:invoiceFlow:exportDialog:workingTitle")}
      </DialogTitle>

      <DialogContent>
        <Grid container spacing={2}>
          <Grid item xs={12} container justifyContent="center">
            <CircularProgress variant="indeterminate" size="64px" style={{ marginTop: "44px", marginBottom: "44px" }} />
          </Grid>
        </Grid>
      </DialogContent>
    </>
  );
}